import { css } from '@emotion/react'
import styled from '@emotion/styled'
import type Sharpie from '@mfc/sharpie'
import {
    CSSProperties,
    memo,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { closeDialog } from '../../state/modals/state.dialog'
import { openTipsDrawer } from '../../state/modals/state.drawer'
import { closeWhiteboard } from '../../state/state.whiteboard'
import Button from '../Buttons/Button'
import ButtonDialogClose from '../Buttons/ButtonDialogClose'
import { AppStateType, useAppStateSelector } from '../Hooks/useAppState'
import { useModelColor } from '../Hooks/useModelColor'
import { useUserSelector } from '../Hooks/useUser'
import NameAvatar from '../common/NameAvatar'
import { getThemeSpacing } from '../styles/appTheme'
import { palette } from '../styles/colors'
import { PAPER_HEIGHTS } from '../theme'
import Loading from './Loading'

export const WhiteboardRoot = styled('div')({
    height: '100%',
    borderTop: `1px solid ${palette.drawerDivider}`,
})
const StyledRoot = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
})
const StyledContainer = styled('div')({
    height: '100%',
    width: '100%',
    overflowX: 'hidden',
    overflowY: 'auto',
    zIndex: PAPER_HEIGHTS.base,
    // ...NO_SCROLLBAR_STYLES,
})
const StyledButtonWrapper = styled('div')({
    textAlign: 'center',
    padding: getThemeSpacing(2),
})

const zIndexCss = css({
    zIndex: 9000,
})
const titleStyles = css({
    borderBottom: '1px solid ' + palette.drawerDivider,
})
const embeddedTitleStyles = css({
    position: 'relative',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: 36,
    padding: 0,
    // background: palette.background,
    borderBottom: '1px solid ' + palette.drawerDivider,
    '& h6': {
        fontSize: 18,
        paddingBottom: 8,
        paddingRight: 8,
    },
    '& div': { marginTop: 0 },
    'div.MuiGrid-root': {
        marginTop: 0,
        paddingTop: 2,
    },
    'div.MuiGrid-item': {
        paddingLeft: 0,
    },
})
const quillEditorContainerStyles = css({
    flexShrink: 0,
    height: '100%',
    width: '100%',
    padding: 0,
    border: '0px !important',
})
const embeddedQuillEditorContainerExtraStyles = css({
    marginTop: getThemeSpacing(4.5),
})
const loadingStyles = css({
    position: 'unset',
    padding: 16,
    paddingBottom: 0,
})
const hiddenStyle = { display: 'none' } as const

const quillOpts = (uniqueId: number) => ({
    modules: {
        toolbar: {
            container: `#toolbar${uniqueId}`,
        },
    },
    formats: [
        'align',
        'size',
        'font',
        'list',
        'strike',
        'color',
        'underline',
        'italic',
        'bold',
        'background',
        'link',
        'video',
        'width',
        'height',
    ],
})

type ToolbarProps = {
    uniqueId: number
}

const Toolbar = memo(({ uniqueId }: ToolbarProps) => (
    <div id={'toolbar' + uniqueId} style={hiddenStyle}>
        <span className="ql-formats">
            <select className="ql-color" defaultValue={''} css={zIndexCss}>
                <option></option>
                <option value="red"></option>
                <option value="maroon"></option>
                <option value="orange"></option>
                <option value="yellow"></option>
                <option value="lime"></option>
                <option value="green"></option>
                <option value="blue"></option>
                <option value="purple"></option>
                <option value="lavender"></option>
                <option value="aqua"></option>
                <option value="brown"></option>
                <option value="grey"></option>
                <option value="magenta"></option>
                <option value="custom-color"></option>
            </select>
            <select className="ql-background" defaultValue={''} css={zIndexCss}>
                <option></option>
                <option value="red"></option>
                <option value="maroon"></option>
                <option value="orange"></option>
                <option value="yellow"></option>
                <option value="lime"></option>
                <option value="green"></option>
                <option value="blue"></option>
                <option value="purple"></option>
                <option value="lavender"></option>
                <option value="aqua"></option>
                <option value="brown"></option>
                <option value="grey"></option>
                <option value="magenta"></option>
                <option value="custom-color"></option>
            </select>
        </span>
        <span className="ql-formats">
            <button className="ql-bold"></button>
            <button className="ql-italic"></button>
            <button className="ql-underline"></button>
            <button className="ql-strike"></button>
            <button className="ql-list" value="ordered"></button>
            <button className="ql-list" value="bullet"></button>
            <button className="ql-clean"></button>
            <select className="ql-align" css={zIndexCss}></select>
        </span>
        <span className="ql-formats">
            <select className="ql-size" defaultValue={''} css={zIndexCss}>
                <option value="small"></option>
                <option></option>
                <option value="large"></option>
                <option value="huge"></option>
            </select>
            <select className="ql-font" css={zIndexCss}></select>
        </span>
        <span className="ql-formats">
            <button className="ql-link"></button>
            <button className="ql-video"></button>
        </span>
        <select className="ql-document-background" css={zIndexCss}>
            <option value="#FFFFFF"></option>
            <option value="#F08080"></option>
            <option value="#778899"></option>
            <option value="#FFA07A"></option>
            <option value="#90EE90"></option>
            <option value="#ADD8E6"></option>
            <option value="#FFFFE0"></option>
        </select>
    </div>
))

type WhiteboardProps = {
    identifier: string | number
    hideTipButton?: boolean
    hideTitle?: boolean
    loading?: boolean
    showAvatar?: boolean
    style?: CSSProperties
    scrollIntoView?(): void
    onClose?(): void
}

const Whiteboard = memo((props: WhiteboardProps) => {
    const sharpie = useRef<Sharpie>(null)
    const [loaded, setLoaded] = useState(false)
    const [uniqueId, setUniqueId] = useState<number>(0)
    const unmounting = useRef(false)
    const prevLoading = useRef(false)
    const firstRender = useRef(true)
    const sharpieLoading = useRef(false)
    const nm = useUserSelector('me', 'name')
    const pass = useAppStateSelector(AppStateType.APP, 'passcode')

    const username = nm?.toLowerCase()?.startsWith('guest') ? '' : nm
    const passcode = !pass || pass === 'guest' ? '' : pass
    const {
        hideTipButton,
        hideTitle,
        loading = false,
        identifier,
        style,
        showAvatar = true,
        scrollIntoView,
        onClose,
    } = props
    const id = useUserSelector(identifier, 'id')
    const channel = useUserSelector(identifier, 'channel')
    const color = useUserSelector(identifier, 'color')
    const hasWhiteboard = useUserSelector(identifier, 'hasWhiteboard')
    const isBroadcasting = useUserSelector(identifier, 'isBroadcasting')
    const isOffline = useUserSelector(identifier, 'isOffline')
    const name = useUserSelector(identifier, 'name')
    const slug = useUserSelector(identifier, 'slug')

    const modelColor = useModelColor(color)
    const embeddedWhiteboard = !!onClose
    const title = hasWhiteboard ? 'WHITEBOARD' : 'has no Whiteboard'

    const resolvedQuillEditorContainerStyles = [quillEditorContainerStyles]
    if (embeddedWhiteboard || hideTitle) {
        resolvedQuillEditorContainerStyles.push(
            embeddedQuillEditorContainerExtraStyles,
        )
    }

    const handleClose = useCallback(() => {
        onClose?.()
        setLoaded(false)
        if (sharpie.current && !sharpie.current.closing) {
            sharpie.current.close()
        }
    }, [onClose])

    const onOpenTip = useCallback(() => {
        if (!embeddedWhiteboard) {
            closeDialog(true)
            setLoaded(false)
            closeWhiteboard()
            if (sharpie.current && !sharpie.current.closing) {
                sharpie.current.close()
            }
        }
        openTipsDrawer({ slug })
    }, [slug, embeddedWhiteboard])

    const setupSharpie = useCallback(async () => {
        if (!hasWhiteboard || !uniqueId) return
        if (sharpieLoading.current) return
        sharpieLoading.current = true
        const { default: Sharpie } = await import('@mfc/sharpie')
        try {
            sharpie.current = new Sharpie(
                `#editor${uniqueId}`,
                'wss://board.myfreecams.com',
                username,
                passcode,
                String(channel),
                quillOpts(uniqueId),
            )
            sharpie.current?.doc?.on?.('load', () => {
                sharpieLoading.current = false
                if (!unmounting.current) {
                    setLoaded(true)
                }
            })
        } catch (e) {
            sharpieLoading.current = false
            console.error(e)
        }
    }, [hasWhiteboard, uniqueId, username, passcode, channel])

    useEffect(() => {
        if (firstRender.current) {
            scrollIntoView?.()
        }
        firstRender.current = false
        return () => {
            firstRender.current = true
        }
    }, [scrollIntoView])

    useEffect(() => {
        unmounting.current = false
        return () => {
            unmounting.current = true
            sharpieLoading.current = false
            if (sharpie.current && !sharpie.current.closing) {
                sharpie.current.close()
            }
            // setUniqueId(0)
        }
    }, [identifier])

    useEffect(() => {
        if (hasWhiteboard) setUniqueId(Math.floor(Math.random() * 1000000))
        else setUniqueId(0)
    }, [id, hasWhiteboard])

    useEffect(() => {
        if (uniqueId > 0) {
            setupSharpie()
        }
    }, [uniqueId])

    useEffect(() => {
        if (!firstRender.current && prevLoading.current && !loading) {
            // socket was connecting but now it's not
            setupSharpie()
        } else if (loaded && loading) {
            // if the socket disconnected
            setLoaded(false)
        }
        prevLoading.current = loading
    }, [loading])
    // return <Loading />

    return (
        <StyledRoot>
            <StyledContainer
                className="no-scrollbars"
                data-testid="Container__Whiteboard"
            >
                {!hideTitle && (
                    <section
                        css={
                            embeddedWhiteboard
                                ? embeddedTitleStyles
                                : titleStyles
                        }
                        data-testid="Whiteboard__Title"
                    >
                        <NameAvatar
                            showAvatar={showAvatar}
                            broadcasting={isBroadcasting}
                            title={title}
                            offline={isOffline}
                            color={modelColor}
                            name={
                                showAvatar || !hasWhiteboard
                                    ? name
                                    : `${name}'s`
                            }
                            id={id}
                            embedded={embeddedWhiteboard || hideTipButton}
                            small={embeddedWhiteboard || hideTipButton}
                        />
                        {embeddedWhiteboard && (
                            <ButtonDialogClose
                                onClick={handleClose}
                                noBackground
                                embedClose
                            />
                        )}
                    </section>
                )}
                {!loaded && hasWhiteboard && <Loading css={loadingStyles} />}
                <Toolbar key={`toolbar${uniqueId}-${id}`} uniqueId={uniqueId} />
                <div
                    key={`editor${uniqueId}-${id}`}
                    id={'editor' + uniqueId}
                    css={resolvedQuillEditorContainerStyles}
                    frameBorder="0"
                    style={style}
                />
            </StyledContainer>
            {!hideTipButton && (
                <StyledButtonWrapper>
                    <Button
                        data-testid="Whiteboard__TipButton"
                        onClick={onOpenTip}
                        size="large"
                    >
                        Tip {name}
                    </Button>
                </StyledButtonWrapper>
            )}
        </StyledRoot>
    )
})

export default Whiteboard
